import React, { FC, Fragment, useState } from "react";
import { Button, makeStyles } from "@material-ui/core";
import environment from "Configs/environment";
import { Auth0Client } from "@auth0/auth0-spa-js";
import { ExternalChatOrganization } from "domain-entities";
import { useTranslation } from "Providers/TranslationProvider";
import { useApp } from "Providers/AppProvider";
import LoadingIndicator from "Components/Layout/LoadingIndicator";
import ErrorView from "Components/Layout/ErrorView";
import { px } from "Configs/theme";
import SuccessIndicator from "../Layout/SuccessIndicator";
import { TrackingEvent, useTracking } from "../../Providers/TrackingProvider";
import { ModalController } from "../Modal/Modal";

interface Credential {
  authenticationType: string;
  domain: string;
  clientId: string;
}

const credentials: Credential[] = [
  {
    authenticationType: "smoothop.io",
    domain: "auth.smoothop.io",
    clientId: "KMx4d2aoeMnx1PEIvbvGm05KMEv0y9iU",
  },
  {
    authenticationType: "gcchat-staging",
    domain: "gcchat-staging.eu.auth0.com",
    clientId: "ZiLPCerU6oCQorGWoOQN55xhpFDMTdE3",
  },
  {
    authenticationType: "gcchat-rc",
    domain: "gcchat-rc.eu.auth0.com",
    clientId: "koSY9098xQZxwSoo64CL9eVPAeqBYRWf",
  }, {
    authenticationType: "none",
    domain: "",
    clientId: "",
  },
];

interface Props {
  organization: ExternalChatOrganization;
  authenticate: (token: string) => Promise<void>;
  modalController: ModalController<ExternalChatOrganization>;
}

const OrganizationSignInGcView: FC<Props> = (props) => {
  const {t} = useTranslation();
  const classes = useStyles();
  const {isStandaloneApp} = useApp();
  //TODO: Refactor code to change the hook from useState to useReducer
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error>();
  const [success, setSuccess] = useState(false);
  const {trackEvent} = useTracking();

  function closeGCViewModel() {
    setTimeout(() => {
      props.modalController.close();
    }, 2000)
  }

  async function authenticate() {
    trackEvent(TrackingEvent.START_AUTH_CTA_CLICKED);
    try {
      setIsLoading(true);
      setError(undefined);
      const credential = credentials.find(
          (c) => props.organization.authenticationType === c.authenticationType
      );

      if (credential?.authenticationType === 'none') {
        await props.authenticate("");
        setIsLoading(false);
        setSuccess(true);
        trackEvent(TrackingEvent.AUTHENTICATION_SUCCEEDED);
        closeGCViewModel();
      } else if (credential) {
        const auth0 = new Auth0Client({
          domain: credential.domain,
          client_id: credential.clientId,
          scope: "openid",
          organizationId: props.organization.id,
          redirect_uri: isStandaloneApp
              ? environment.standaloneAppUrl
              : `${environment.mainAppUrl}/#/external-chat`,
          authorizeTimeoutInSeconds: 20 * 60 // Converting seconds into minutes
        });

        await auth0.loginWithPopup({
          prompt: 'login'
        });
        const idToken = await auth0.getIdTokenClaims();

        await props.authenticate(idToken.__raw);
        setIsLoading(false);
        setSuccess(true);
        trackEvent(TrackingEvent.AUTHENTICATION_SUCCEEDED);
        closeGCViewModel();
      } else {
        throw new Error(
          `Unknown authentication type "${props.organization.authenticationType}".`
        );
      }
    } catch (error) {
      setError(error);
      setIsLoading(false);
      setSuccess(false);
    }
  }

  if (isLoading) {
    return <LoadingIndicator/>;
  } else if (success) {
    return <SuccessIndicator/>;
  } else {
    return (
        <Fragment>
          <Button
              className={classes.button}
              onClick={authenticate}
              variant="contained"
              color="primary"
              disableElevation
          >
            {t("OrganizationSignInView_Cta")}
          </Button>
          {error !== undefined && <ErrorView error={error}/>}
        </Fragment>
    );
  }
};

export default OrganizationSignInGcView;

const useStyles = makeStyles({
  button: {
    fontSize: "14px",
    fontWeight: "bold",
    marginBottom: px.m,
  },
});
